import { type ChangeEvent, type DragEvent, useRef, useState } from 'react';
import { PrototypeIcon } from './PrototypeIcon';
import {
  freebeIntegrationApi,
  type DeclarationImportMode,
  type FreebeDeclarationPreviewResult,
} from './freebe-integration-api';

const MAX_ARCHIVE_BYTES = 15_728_640;

interface FreebeDeclarationsCardProps {
  readonly csrfToken: string;
  readonly enabled: boolean;
  readonly onImported?: () => void;
}

/** La prévisualisation ne confirme ni le dépôt URSSAF, ni les sommes transcrites par Freebe. */
export function FreebeDeclarationsCard({
  csrfToken,
  enabled,
  onImported,
}: FreebeDeclarationsCardProps): React.JSX.Element {
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useState<DeclarationImportMode>('historical');
  const [preview, setPreview] = useState<FreebeDeclarationPreviewResult | null>(null);
  const [pending, setPending] = useState<'preview' | 'import' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  function select(candidate: File | null): void {
    setFile(candidate);
    setPreview(null);
    setResult(null);
    setError(
      candidate !== null &&
        (!candidate.name.toLowerCase().endsWith('.zip') ||
          candidate.size === 0 ||
          candidate.size > MAX_ARCHIVE_BYTES)
        ? 'Sélectionnez un export Déclarations ZIP de 15 Mio maximum.'
        : null,
    );
  }

  function change(event: ChangeEvent<HTMLInputElement>): void {
    select(event.currentTarget.files?.[0] ?? null);
  }

  function drop(event: DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    if (!enabled || pending !== null) return;
    select(event.dataTransfer.files[0] ?? null);
  }

  const validFile =
    file !== null &&
    file.name.toLowerCase().endsWith('.zip') &&
    file.size > 0 &&
    file.size <= MAX_ARCHIVE_BYTES;

  async function inspect(): Promise<void> {
    if (!enabled || !validFile || file === null || pending !== null) return;
    setPending('preview');
    setPreview(null);
    setResult(null);
    setError(null);
    try {
      setPreview(await freebeIntegrationApi.previewDeclarationArchive(file, mode, csrfToken));
    } catch {
      setError(
        'Archive non reconnue ou connexion indisponible. Vérifiez l’export Freebe avec pièces jointes.',
      );
    } finally {
      setPending(null);
    }
  }

  async function apply(): Promise<void> {
    if (!enabled || !validFile || file === null || preview === null || pending !== null) return;
    setPending('import');
    setError(null);
    try {
      const imported = await freebeIntegrationApi.importDeclarationArchive(
        file,
        mode,
        preview.archiveSha256,
        csrfToken,
      );
      setResult(
        `${imported.imported} déclaration(s) ajoutée(s), ${imported.unchanged} déjà connues. ` +
          'Les PDF sont lus automatiquement pour les calculs. Les indications de paiement Freebe restent distinctes des débits bancaires.',
      );
      onImported?.();
      setPreview(null);
      setFile(null);
      if (input.current !== null) input.current.value = '';
    } catch {
      setError(
        'L’import n’a pas abouti. Aucune somme n’est confirmée ; une relance du même ZIP reprend sans doublon.',
      );
    } finally {
      setPending(null);
    }
  }

  return (
    <article
      id="freebe-declarations-section"
      className="freebe-integration__dataset freebe-declarations"
    >
      <span className="freebe-integration__dataset-icon" aria-hidden="true">
        <PrototypeIcon name="file-text" />
      </span>
      <span className="freebe-integration__dataset-copy">
        <strong>Déclarations URSSAF</strong>
        <small>Export Freebe ZIP avec JSON et PDF — pièces privées</small>
      </span>
      <span className="freebe-integration__dataset-state">JSON + PDF</span>
      <div className="freebe-declarations__content">
        <p>
          Les PDF officiels font foi. L’indication de paiement Freebe est distincte de la preuve
          bancaire.
        </p>
        <div className="freebe-declarations__modes">
          <span data-active={mode === 'historical'}>Historique</span>
          <button
            type="button"
            role="switch"
            aria-label="Année courante uniquement"
            aria-checked={mode === 'current_year'}
            disabled={!enabled || pending !== null}
            onClick={() => {
              setMode(mode === 'historical' ? 'current_year' : 'historical');
              setPreview(null);
              setResult(null);
            }}
          >
            <span aria-hidden="true" />
          </button>
          <span data-active={mode === 'current_year'}>Année courante</span>
        </div>
        <div
          className="freebe-declarations__dropzone"
          onDragOver={(event) => event.preventDefault()}
          onDrop={drop}
        >
          <input
            ref={input}
            id="freebe-declarations-file"
            type="file"
            accept=".zip,application/zip"
            hidden
            disabled={!enabled || pending !== null}
            onChange={change}
          />
          <button
            type="button"
            disabled={!enabled || pending !== null}
            onClick={() => input.current?.click()}
          >
            Parcourir
          </button>
          <small>ou glisser-déposer le ZIP ici · 15 Mio max.</small>
        </div>
        {file !== null ? (
          <small className="freebe-declarations__filename" title={file.name}>
            {file.name}
          </small>
        ) : null}
        <button
          type="button"
          disabled={!enabled || !validFile || pending !== null}
          onClick={() => void inspect()}
        >
          {pending === 'preview' ? 'Contrôle en cours…' : 'Prévisualiser l’import'}
        </button>
        {preview !== null ? (
          <div className="freebe-declarations__preview" role="status">
            <strong>{preview.declarationCount} déclaration(s) à examiner</strong>
            <span>
              Périodes source : {preview.firstSourcePeriod ?? '—'} à{' '}
              {preview.lastSourcePeriod ?? '—'}.
            </span>
            <span>
              {preview.missingPdf} PDF absent(s) ; {preview.freebeDraftCount} ligne(s) marquée(s) «
              brouillon » par Freebe.
            </span>
            {preview.excludedOutsideCurrentYear > 0 ? (
              <span>
                {preview.excludedOutsideCurrentYear} période(s) hors année courante écartée(s).
              </span>
            ) : null}
            <small>
              Les montants officiels seront lus sur les PDF. Aucun débit bancaire n’est créé.
            </small>
            <button
              type="button"
              disabled={!enabled || pending !== null || preview.declarationCount === 0}
              onClick={() => void apply()}
            >
              {pending === 'import' ? 'Import en cours…' : 'Confirmer l’import des preuves'}
            </button>
          </div>
        ) : null}
        {error !== null ? (
          <p className="freebe-declarations__error tosho-feedback is-error" role="alert">
            {error}
          </p>
        ) : null}
        {result !== null ? (
          <p className="freebe-declarations__result tosho-feedback is-success" role="status">
            {result}
          </p>
        ) : null}
        <small>
          Les déclarations URSSAF ne font pas partie de la MAJ globale ni de la planification.
          L’import ZIP reste manuel.
        </small>
      </div>
    </article>
  );
}
